import { FileText } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";

const LAST_UPDATED = "28 August 2026";

const TermsOfService = () => {
  const { locale } = useI18n();
  const isIt = locale === "it";

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30">
      <div className="page-container mx-auto py-12 space-y-8 max-w-4xl">
        <div className="glass-card p-8 space-y-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold">{isIt ? "Termini di Servizio" : "Terms of Service"}</h1>
          <p className="text-sm text-muted-foreground">
            {isIt ? "Ultimo aggiornamento" : "Last updated"}: {LAST_UPDATED}
          </p>
        </div>

        <div className="glass-card p-8 space-y-6 text-sm leading-relaxed text-muted-foreground">
          {isIt ? (
            <>
              <Section title="1. Accettazione dei termini">
                Utilizzando NATIBOT ("il Servizio"), invitandolo su un server Discord o accedendo alla dashboard web,
                accetti di essere vincolato dai presenti Termini di Servizio. Se non accetti questi termini, non
                utilizzare il Servizio.
              </Section>
              <Section title="2. Descrizione del Servizio">
                NATIBOT è un bot Discord che offre funzionalità di moderazione, automazioni, engagement della
                community e strumenti di gestione tramite una dashboard web. Il Servizio è fornito "così com'è" e può
                essere modificato o interrotto in qualsiasi momento.
              </Section>
              <Section title="3. Requisiti di utilizzo">
                Devi rispettare i{" "}
                <a
                  href="https://discord.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Termini di Servizio di Discord
                </a>{" "}
                e le relative linee guida della community. Non è consentito utilizzare il Servizio per attività
                illegali, dannose, moleste o che violino i diritti di terzi.
              </Section>
              <Section title="4. Account e permessi">
                L'installazione e la configurazione del bot su un server richiedono permessi amministrativi adeguati.
                Sei responsabile delle azioni intraprese tramite il tuo account e delle configurazioni applicate al
                tuo server.
              </Section>
              <Section title="5. Piani Premium">
                Alcune funzionalità sono disponibili solo tramite abbonamento Premium. I pagamenti sono gestiti da
                fornitori terzi e sono soggetti alle rispettive condizioni. Gli abbonamenti si rinnovano
                automaticamente salvo disdetta.
              </Section>
              <Section title="6. Sospensione e cessazione">
                Ci riserviamo il diritto di sospendere o rimuovere il bot da un server, o limitare l'accesso alla
                dashboard, in caso di violazione di questi Termini o dei Termini di Servizio di Discord.
              </Section>
              <Section title="7. Limitazione di responsabilità">
                Il Servizio è fornito senza garanzie di alcun tipo. Nella misura massima consentita dalla legge, non
                siamo responsabili per danni indiretti, incidentali o consequenziali derivanti dall'uso del Servizio.
              </Section>
              <Section title="8. Modifiche ai Termini">
                Potremmo aggiornare questi Termini periodicamente. L'uso continuato del Servizio dopo eventuali
                modifiche costituisce accettazione dei nuovi Termini.
              </Section>
              <Section title="9. Legge applicabile">
                Questi Termini sono regolati dalla legge applicabile nella giurisdizione in cui operiamo, salvo
                diversa disposizione di legge inderogabile.
              </Section>
              <Section title="10. Contatti">
                Per domande sui presenti Termini, contattaci tramite la pagina{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contattaci
                </a>
                .
              </Section>
            </>
          ) : (
            <>
              <Section title="1. Acceptance of Terms">
                By using NATIBOT ("the Service"), inviting it to a Discord server, or accessing the web dashboard,
                you agree to be bound by these Terms of Service. If you do not agree, do not use the Service.
              </Section>
              <Section title="2. Description of Service">
                NATIBOT is a Discord bot offering moderation, automation, community engagement features, and
                management tools via a web dashboard. The Service is provided "as is" and may be modified or
                discontinued at any time.
              </Section>
              <Section title="3. Usage Requirements">
                You must comply with{" "}
                <a
                  href="https://discord.com/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Discord's Terms of Service
                </a>{" "}
                and community guidelines. You may not use the Service for illegal, harmful, harassing activities or
                activities that infringe on the rights of others.
              </Section>
              <Section title="4. Accounts & Permissions">
                Installing and configuring the bot on a server requires appropriate administrative permissions. You
                are responsible for actions taken through your account and the configurations applied to your
                server.
              </Section>
              <Section title="5. Premium Plans">
                Some features are only available through a Premium subscription. Payments are handled by third-party
                providers and are subject to their respective terms. Subscriptions renew automatically unless
                cancelled.
              </Section>
              <Section title="6. Suspension & Termination">
                We reserve the right to suspend or remove the bot from a server, or restrict access to the
                dashboard, in case of violation of these Terms or Discord's Terms of Service.
              </Section>
              <Section title="7. Limitation of Liability">
                The Service is provided without warranties of any kind. To the maximum extent permitted by law, we
                are not liable for indirect, incidental, or consequential damages arising from use of the Service.
              </Section>
              <Section title="8. Changes to Terms">
                We may update these Terms periodically. Continued use of the Service after changes constitutes
                acceptance of the new Terms.
              </Section>
              <Section title="9. Governing Law">
                These Terms are governed by the applicable law of the jurisdiction in which we operate, unless
                otherwise required by mandatory law.
              </Section>
              <Section title="10. Contact">
                For questions about these Terms, contact us via the{" "}
                <a href="/support/contact" className="text-primary hover:underline">
                  Contact Us
                </a>{" "}
                page.
              </Section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div className="space-y-2">
    <h2 className="text-lg font-semibold text-foreground">{title}</h2>
    <p>{children}</p>
  </div>
);

export default TermsOfService;
